/*
 * Copyright (c) Nexign, JSC, 2020-2025.
 */
/**
 * Tariff and services product tariff types
 *
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.39.0
 * @since 1.10.0
 */

import { EBorder, EHead, IBadge, ICardContentParams } from '@nexign/scc-ui-kit/lib/components/RatePlanCard/types';

import { IProductCard, IProductCardResponse } from './tariffsAndServicesProductTypes';

/*
 * -------------------------------------------
 *              API
 * -------------------------------------------
 */

export interface IProductCardTariffResponse extends IProductCardResponse {
    // раздел "Дополнительно", наполнение которого скрываем под инфозначком
    benefits?: IProductCardTariffField<IProductCardTariffFieldBenefits[]>;
    // инфо по кешбэку (отображается под АП)
    cashback?: IProductCardTariffField<IProductCardTariffFieldPrice>;
    // раздел для интернет-трафика. Используется в пакетах с ЛИМИТНЫМ трафиком
    internetLimited?: IProductCardTariffField<IProductCardTariffFieldInternet[]>;
    // раздел для интернет-трафика. Используется в пакетах с БЕЗЛИМИТНЫМ трафиком.
    internetUnlimited?: IProductCardTariffField<IProductCardTariffFieldInternet[]>;
    // Абонентская плата
    payment?: IProductCardTariffField<IProductCardTariffFieldPrice>;
    // range дяя выбора минут и трафика
    ranges?: IProductCardTariffFieldRange[];
    // раздел "Базовый состав пакета"
    roamings?: IProductCardTariffField<IProductCardTariffFieldRoaming[]>;
    // раздел Отображение информации по количеству доступных подключений в карточке непубличного предложения
    // лимит доступных подключений
    limitConnections?: number;
    // количество фактических подключений
    connected?: number;

}

export interface IProductNewCardTariffResponse
    extends Omit<
        IProductCardTariffResponse,
        'benefits' | 'internetUnlimited' | 'internetLimited' | 'roamings' | 'payment'
    > {
    benefits?: IProductCardTariffField<IWeightedValueItem[]>;
    roamings?: IProductCardTariffField<IWeightedValueItem[]>;
    internetUnlimited?: IProductCardTariffField<IWeightedValueItem[]>;
    internetLimited?: IProductCardTariffField<IWeightedValueItem[]>;
    includedTariff?: IProductCardTariffField<IWeightedValueItem[]>;
    megaPowers?: IProductCardTariffField<Omit<IWeightedValueItem, 'weight'>>;
    badge?: IBadge;
    kollektiv?: IProductCardTariffField<string>;
    device?: IProductCardTariffField<string>;
    payment?: IProductCardTariffField<IProductCardTariffFieldPrice>;
    lineTitle?: string;
    head?: EHead;
    border?: EBorder;
}

/*
 * -------------------------------------------
 *              Tariff Props
 * -------------------------------------------
 */

export interface IProductCardTariff extends IProductCard {
    // range дяя выбора минут и трафика
    ranges?: IProductCardTariffFieldRange[];
    // раздел "Базовый состав пакета"
    roamings?: IProductCardTariffField<IProductCardTariffFieldRoaming[]>;
    // раздел для интернет-трафика. Используется в пакетах с БЕЗЛИМИТНЫМ трафиком.
    internetUnlimited?: IProductCardTariffField<string[]>;
    // раздел для интернет-трафика. Используется в пакетах с ЛИМИТНЫМ трафиком
    internetLimited?: IProductCardTariffField<string[]>;
    // раздел "Дополнительно", наполнение которого скрываем под инфозначком
    benefits?: IProductCardTariffField<string[]>;
    // Абонентская плата
    payment?: IProductCardTariffField<IProductCardTariffFieldPrice>;
    // инфо по кешбэку (отображается под АП)
    cashback?: IProductCardTariffField<IProductCardTariffFieldPrice>;
    // раздел Отображение информации по количеству доступных подключений в карточке непубличного предложения
    // лимит доступных подключений
    limitConnections?: number;
    // количество фактических подключений
    connected?: number;
}

/*
 * -------------------------------------------
 *              Tariff Field
 * -------------------------------------------
 */

export interface IProductCardTariffField<V> {
    title: string;
    value: V;
}

export enum EProductCardTariffRoamingType {
    Calls = 'CALLS',
    Internet = 'INTERNET',
    InternetLimited = 'INTERNET_LIMITED',
    SMS = 'SMS',
}

// раздел "Базовый состав пакета"
export interface IProductCardTariffFieldRoaming {
    // Подсказка только для непубличных тарифов
    routeIds?: number[]; // id для показа подсказки для пакета
    // Url приходит только для публичных тарифов
    svgIconUrl?: string; // ссылка на иконку для   SMS | по звонкам |  по межгороду
    // Тип приходит только для непубличных тарифов
    type?: EProductCardTariffRoamingType;
    value: string; // showcase_params.children.value || showcase_params.children.title
    weight: number;
}

// range дяя выбора минут и трафика
export interface IProductCardTariffFieldRange {
    id: number; // service_packs.billing_id
    calls: number; // service_packs.calls
    callsUnit: string; // service_packs.calls_unit
    traffic: number; // service_packs.traffic
    trafficUnit: string; // service_packs.traffic_unit
    weight: number; // service_packs.order
}

export interface IProductCardTariffFieldInternet {
    value: string;
    weight: number;
}

export interface IProductCardTariffFieldBenefits {
    value: string;
    weight: number;
}

export interface IProductCardTariffFieldPrice {
    // название
    value: number; // monthly_payment.value | cashback_mes.value
    unit?: string; // monthly_payment.unit | cashback_mes.unit
    unitExtra?: string; // for campaign rateplans 2025
    unitValue?: string; // for campaign rateplans 2025
}

/*
 * -------------------------------------------
 *              Tariff Selected
 * -------------------------------------------
 */

export interface IProductCardTariffsRangeSelected {
    [id: number]: number;
}

export interface IProductCardTariffSetRange {
    id: number;
    rangeId: number;
}

/*
 * -------------------------------------------
 *              New Tariff Cards Props
 * -------------------------------------------
 */

interface IWeightedValueItem {
    value: string;
    weight: number;
    svgIconUrl: string;
    footnote: string;
}

export interface ICardTariffNewItem
    extends Omit<
        IProductCardTariff,
        'benefits' | 'internetUnlimited' | 'internetLimited' | 'cashback' | 'roamings' | 'payment'
    > {
    lineTitle?: string;
    head?: EHead;
    border?: EBorder;
    badge?: IBadge;
    payment?: IProductCardTariffField<Partial<IProductCardTariffFieldPrice>>;
    includedTariff?: IProductCardTariffField<Array<Partial<IWeightedValueItem>>>;
    shortDescription?: string;
    megaPowers?: IProductCardTariffField<Partial<IWeightedValueItem>>;
    roamings?: IProductCardTariffField<Array<Partial<IWeightedValueItem>>>;
    kollektiv?: IProductCardTariffField<string>;
    device?: IProductCardTariffField<string>;
    benefits?: IProductCardTariffField<Array<Partial<IWeightedValueItem>>>;
    internetUnlimited?: IProductCardTariffField<Array<Partial<IWeightedValueItem>>>;
    internetLimited?: IProductCardTariffField<Array<Partial<IWeightedValueItem>>>;
    formattedCashback?: IProductCardTariffField<IProductCardTariffFieldPrice>;
    rowParams?: ICardContentParams;
}
